import React from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import { format } from "date-fns";
import { selectCustomStyles } from "../../../../assets/global/selectCustomStyles";
import CustomDatePicker from "../../../../components/CustomDatePicker";
import DateInput from "../../../../components/inputs/DateInput";
import { formatCurrency } from "../../../../utils/numberFormatter";
import CheckNumberError from "../../../../components/inputs/CheckNumberError";

export const PAYMENT_METHODS = ["Cash", "Bank"];

// Empty source of funds; Cash is the default payment method
export const emptySource = () => ({
  paymentMethod: "Cash",
  subject: null,
  account: null,
  chequeNumber: "",
  maturityDate: "",
  remarks: "",
});

// Account (account_list_sub3) -> react-select option, keeping the record
export const toAccountOption = (account) => ({
  value: account.id,
  label: account.account_name,
  account,
});

// Bank needs a maturity date on top of the account. The cheque number is
// optional: with one the payment is an issued check, without one a bank
// transaction.
export const isSourceComplete = (source) =>
  Boolean(source?.account) &&
  (source.paymentMethod !== "Bank" || Boolean(source.maturityDate));

// One-line description, e.g. "Bank · BDO > Savings · Chq #123 · Oct 01, 2026"
export const describeSource = (source) =>
  [
    source.paymentMethod,
    `${source.subject?.label || "--"} > ${source.account?.label || "--"}`,
    source.paymentMethod === "Bank" && source.chequeNumber
      ? `Chq #${source.chequeNumber}`
      : null,
    source.paymentMethod === "Bank" && source.maturityDate
      ? format(new Date(source.maturityDate), "MMM dd, yyyy")
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

// Menus render in document.body so a modal or table doesn't clip them
const portalStyles = (value, validated) => ({
  ...selectCustomStyles(value, validated),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
});

// Payment method, subject, account, cheque details and remarks of a source
// of funds. Subjects are filtered by subject_type to match the method.
function SourceAccountFields({
  idPrefix,
  value,
  onChange,
  subjects,
  accountsBySubject,
  loadAccounts,
  excludeAccountIds = new Set(),
  validated = false,
  // Optional extra field (e.g. the split line's Amount) shown in the same row
  // as Subject and Account
  sideField = null,
  // Cheque numbers of the other lines in this form, and the ones already
  // saved on this profit distribution (they may be shared, so not looked up)
  chequePending = [],
  chequeIgnore = [],
}) {
  const isBank = value.paymentMethod === "Bank";

  // Subject, Account (and sideField) share one row
  const rowColumn = sideField ? "col-12 col-md-4" : "col-12 col-md-6";

  const subjectOptions = subjects
    .filter((subject) => subject.subject_type === value.paymentMethod)
    .map((subject) => ({ value: subject.id, label: subject.subject_name }));

  const accountOptions = (accountsBySubject[value.subject?.value] || [])
    .filter((account) => !excludeAccountIds.has(account.id))
    .map(toAccountOption);

  // Switching method clears everything picked for the other method
  const handleMethodChange = (paymentMethod) => {
    onChange({
      paymentMethod,
      subject: null,
      account: null,
      chequeNumber: "",
      maturityDate: "",
    });
  };

  const handleSubjectChange = (option) => {
    onChange({ subject: option, account: null });
    if (option) loadAccounts(option.value);
  };

  const setMaturityDate = (date) => onChange({ maturityDate: date || "" });

  return (
    <div className="row g-3">
      <div className="col-12">
        <Form.Label className="fw-bold d-block mb-1">Payment Method</Form.Label>
        {PAYMENT_METHODS.map((method) => (
          <Form.Check
            key={method}
            inline
            type="radio"
            id={`${idPrefix}-method-${method}`}
            name={`${idPrefix}-method`}
            label={method}
            checked={value.paymentMethod === method}
            onChange={() => handleMethodChange(method)}
          />
        ))}
      </div>

      <div className={rowColumn}>
        <Form.Label className="fw-bold">Subject</Form.Label>
        <Select
          options={subjectOptions}
          value={value.subject}
          onChange={handleSubjectChange}
          placeholder={`Select ${value.paymentMethod} Subject`}
          styles={portalStyles(value.subject, validated)}
          menuPortalTarget={document.body}
          noOptionsMessage={() =>
            `No ${value.paymentMethod} subject under Account-List`
          }
          isSearchable
          isClearable
        />
      </div>

      <div className={rowColumn}>
        <Form.Label className="fw-bold">Account</Form.Label>
        <Select
          options={accountOptions}
          value={value.account}
          onChange={(option) => onChange({ account: option })}
          placeholder={
            value.subject ? "Select Account" : "Select a subject first"
          }
          styles={portalStyles(value.account, validated)}
          menuPortalTarget={document.body}
          isDisabled={!value.subject}
          isSearchable
          isClearable
        />
        {value.account && (
          <small className="text-muted">
            Account Balance:{" "}
            <strong>
              {formatCurrency(
                value.account.account.amount,
                value.account.account.currency?.currency_name,
              )}
            </strong>
          </small>
        )}
      </div>

      {sideField && <div className={rowColumn}>{sideField}</div>}

      {isBank && (
        <>
          <div className="col-12 col-md-6">
            <Form.Label className="fw-bold">Cheque #</Form.Label>
            <Form.Control
              type="text"
              value={value.chequeNumber}
              onChange={(e) => onChange({ chequeNumber: e.target.value })}
              placeholder="Enter cheque number"
              maxLength={30}
            />
            <CheckNumberError
              value={value.chequeNumber}
              pending={chequePending}
              ignore={chequeIgnore}
            />
            <small className="text-muted">
              Leave blank to pay through a bank transaction instead of a
              cheque.
            </small>
          </div>
          <div className="col-12 col-md-6">
            <Form.Label className="fw-bold">
              Maturity Date <span className="text-danger">*</span>
            </Form.Label>
            <CustomDatePicker
              selected={value.maturityDate ? new Date(value.maturityDate) : ""}
              handleDateChange={setMaturityDate}
              setter={setMaturityDate}
              CustomInput={DateInput}
              isRequired={true}
              validated={validated}
            />
          </div>
        </>
      )}

      <div className="col-12">
        <Form.Label className="fw-bold">Remarks</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={value.remarks}
          onChange={(e) => onChange({ remarks: e.target.value })}
          placeholder="Enter remarks"
          style={{ resize: "none" }}
        />
      </div>
    </div>
  );
}

export default SourceAccountFields;
